import React from 'react';
import { ExternalLink, Bookmark, BookmarkCheck, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ShareButtons from './ShareButtons';
import Reactions from './Reactions';
import Comments from './Comments';

const NewsCard = ({ news, onBookmarkToggle, onDelete, isAdmin }) => {
  const { user } = useAuth();

  const categoryColors = {
    world: '#4ECDC4',
    politics: '#FF6B6B',
    economy: '#FFE66D',
    tech: '#9B59B6',
    science: '#3498DB',
    sports: '#2ECC71',
    celebrity: '#E91E63'
  };

  const handleBookmark = (e) => {
    e.preventDefault();
    if (user) {
      onBookmarkToggle(news.id, news.is_bookmarked);
    }
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm('Delete this article?')) {
      onDelete && onDelete(news.id);
    }
  };

  return (
    <div
      data-testid={`news-card-${news.id}`}
      className="bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] border-2 border-black dark:border-white rounded-xl p-1 hard-shadow-lg mb-6"
    >
      <div className="bg-white dark:bg-[#2D2A26] rounded-lg overflow-hidden">
        {/* Header with category */}
        <div 
          className="px-4 py-2 flex items-center justify-between"
          style={{ backgroundColor: categoryColors[news.category] || '#4ECDC4' }}
        >
          <span className="font-bold text-sm uppercase tracking-wider text-black">
            {news.category}
          </span>
          <div className="flex items-center gap-2">
            {isAdmin && onDelete && (
              <button
                onClick={handleDelete}
                data-testid={`delete-button-${news.id}`}
                className="p-1.5 rounded-full bg-red-500 hover:bg-red-600 transition-all border border-black/20"
                title="Delete article"
              >
                <Trash2 className="w-4 h-4 text-white" strokeWidth={2.5} />
              </button>
            )}
            {user && (
              <button
                onClick={handleBookmark}
                data-testid={`bookmark-button-${news.id}`}
                className="p-1.5 rounded-full bg-white/90 hover:bg-white transition-all border border-black/20"
              >
                {news.is_bookmarked ? (
                  <BookmarkCheck className="w-4 h-4 text-black" strokeWidth={2.5} />
                ) : (
                  <Bookmark className="w-4 h-4 text-black" strokeWidth={2.5} />
                )}
              </button>
            )}
            <ShareButtons newsId={news.id} slug={news.slug} title={news.title} summary={news.humorous_summary} />
          </div>
        </div>

        {/* Image with caption overlay - clickable */}
        <a 
          href={news.source_url || '#'} 
          target="_blank" 
          rel="noopener noreferrer"
          className="block relative cursor-pointer hover:opacity-95 transition-opacity"
        >
          {news.image_url ? (
            <img
              src={news.image_url}
              alt={news.title}
              className="w-full h-64 object-cover"
              onError={(e) => {
                e.target.src = `https://picsum.photos/800/400?random=${news.id}`;
              }}
            />
          ) : (
            <img
              src={`https://picsum.photos/800/400?random=${news.id}`}
              alt={news.title}
              className="w-full h-64 object-cover"
            />
          )}
          
          {/* Caption overlay - the witty summary */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent pt-16 pb-4 px-4">
            <p 
              className="text-white text-lg font-bold leading-snug"
              style={{ fontFamily: 'Merriweather, serif' }}
            >
              "{news.humorous_summary}"
            </p>
          </div>
        </a>

        {/* Footer with title and actions */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          {/* Original title */}
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
            {news.title}
          </p>

          {/* Reactions */}
          <Reactions newsId={news.id} />
          
          {/* Comments */}
          <Comments newsId={news.id} />

          {/* Source link */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
              {news.source}
            </span>
            {news.source_url && (
              <a
                href={news.source_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`source-link-${news.id}`}
                className="flex items-center gap-1 text-xs text-[#4ECDC4] hover:underline font-medium"
              >
                Read full story
                <ExternalLink className="w-3 h-3" strokeWidth={2.5} />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsCard;
